/**
 * ErrorBoundary Component
 *
 * Global error boundary that catches React errors and provides a user-friendly fallback UI.
 * Integrates with error logging and provides recovery options.
 *
 * @version 1.0
 * @created 2025-11-19
 */

import React from 'react';
import { useRouteError, useNavigate, isRouteErrorResponse, useLocation } from 'react-router-dom';
import { logError } from '../../services/errorLoggingService';

/**
 * Root Error Boundary Component
 *
 * Catches all errors in the application and provides:
 * - User-friendly error messages
 * - Recovery actions (reload, go home)
 * - Error details in development
 * - Error logging to monitoring service
 */
export default function ErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();

  // Log error to monitoring service with component context
  React.useEffect(() => {
    logError(error, {
      type: 'route_error',
      route: location.pathname,
      componentName: 'ErrorBoundary',
      componentPath: 'App > ErrorBoundary',
      metadata: {
        search: location.search,
        hash: location.hash,
        state: location.state,
      },
    });
  }, [error, location]);

  // Log error to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error('ErrorBoundary caught error:', error);
  }

  // Determine error type and message
  let errorTitle = 'Something went wrong';
  let errorMessage = 'An unexpected error occurred. Please try again.';
  let errorStatus = null;
  let showDetails = false;

  if (isRouteErrorResponse(error)) {
    // Route-specific errors (404, 401, etc.)
    errorStatus = error.status;

    switch (error.status) {
      case 404:
        errorTitle = 'Page Not Found';
        errorMessage = "The page you're looking for doesn't exist.";
        break;
      case 401:
        errorTitle = 'Unauthorized';
        errorMessage = 'You need to sign in to access this page.';
        break;
      case 403:
        errorTitle = 'Access Denied';
        errorMessage = "You don't have permission to access this page.";
        break;
      case 500:
        errorTitle = 'Server Error';
        errorMessage = 'The server encountered an error. Please try again later.';
        break;
      case 503:
        errorTitle = 'Service Unavailable';
        errorMessage = 'The service is temporarily unavailable. Please try again later.';
        break;
      default:
        errorTitle = `Error ${error.status}`;
        errorMessage = error.statusText || errorMessage;
    }
  } else if (error instanceof Error) {
    // JavaScript errors
    errorMessage = error.message;
    showDetails = process.env.NODE_ENV === 'development';
  }

  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-2xl w-full">
        {/* Error Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
            <svg
              className="w-10 h-10 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {errorStatus && (
            <div className="text-6xl font-bold text-gray-300 mb-2">
              {errorStatus}
            </div>
          )}

          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {errorTitle}
          </h1>

          <p className="text-lg text-gray-600 mb-8">
            {errorMessage}
          </p>
        </div>

        {/* Error Details (Development Only) */}
        {showDetails && error instanceof Error && (
          <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg">
            <h2 className="text-sm font-semibold text-red-800 mb-2">
              Error Details (Development Only)
            </h2>
            <pre className="text-xs text-red-700 overflow-x-auto whitespace-pre-wrap">
              {error.stack || error.message}
            </pre>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleReload}
            className="px-6 py-3 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition font-medium"
          >
            Reload Page
          </button>

          <button
            onClick={handleGoHome}
            className="px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition font-medium"
          >
            Go Home
          </button>

          <button
            onClick={handleGoBack}
            className="px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
          >
            Go Back
          </button>
        </div>

        {/* Support Info */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500">
            If this problem persists, please{' '}
            <a
              href="mailto:support@outdoorguides.com"
              className="text-rose-600 hover:text-rose-700 underline"
            >
              contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Lightweight Error Fallback for Page-Level Errors
 *
 * Use this for page-specific error boundaries where you want
 * a less prominent error display that keeps the page layout.
 */
export function PageErrorFallback() {
  const error = useRouteError();
  const navigate = useNavigate();

  let errorMessage = 'An error occurred while loading this page.';

  if (isRouteErrorResponse(error)) {
    errorMessage = error.statusText || errorMessage;
  } else if (error instanceof Error) {
    errorMessage = error.message;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-sm font-medium text-red-800">
              Error Loading Page
            </h3>
            <p className="mt-1 text-sm text-red-700">
              {errorMessage}
            </p>
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/')}
                className="text-sm font-medium text-red-600 hover:text-red-700"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Network Error Fallback
 *
 * Specific error boundary for network/API failures.
 * Provides retry functionality and offline detection.
 */
export function NetworkErrorFallback() {
  const _error = useRouteError();
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
              />
            </svg>
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-sm font-medium text-yellow-800">
              {isOnline ? 'Connection Problem' : 'No Internet Connection'}
            </h3>
            <p className="mt-1 text-sm text-yellow-700">
              {isOnline
                ? 'Unable to reach the server. Please check your connection and try again.'
                : 'You appear to be offline. Please check your internet connection.'}
            </p>
            {isOnline && (
              <div className="mt-4">
                <button
                  onClick={handleRetry}
                  className="px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-lg hover:bg-yellow-700 transition"
                >
                  Retry Connection
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
